<template>
  <div class="custom-order-stepper">
    <div v-if="orderStatus === 'failed'" class="stepper-container failed-state">
      <div class="failed-connector-line"></div>
      <div class="step-item">
        <div class="step-circle failed">
          <v-icon color="white" size="24">
            mdi-close
          </v-icon>
        </div>
        <div class="step-label active">
          {{ $t('screens.foodCourtOrder.status.failed') }}
        </div>
      </div>
    </div>

    <div v-else-if="orderStatus === 'runner_delivery_pending'" class="stepper-container">
      <div class="step-item">
        <div class="step-circle active">
          <img :src="`/assets/icons/status/circleCheck.svg`" class="step-icon" />
        </div>
        <div class="step-label active">
          {{ $t('screens.foodCourtOrder.status.runnerDeliveryPending') }}
        </div>
      </div>
    </div>

    <div v-else class="stepper-container">
      <div class="step-item">
        <div class="step-circle" :class="getStepClass(1)">
          <img :src="`/assets/icons/status/bell.svg`" class="step-icon" />
        </div>
        <div class="step-label" :class="{ active: currentStep >= 1 }">
          {{ $t('screens.foodCourtOrder.status.received') }}
        </div>
      </div>

      <v-progress-linear
        class="step-connector"
        :color="currentStep >= 1 ? 'green' : '#D9D9D9'"
        height="3"
        :value="currentStep > 1 ? 100 : 0"
        :indeterminate="currentStep === 1"
      />

      <div class="step-item">
        <div class="step-circle" :class="getStepClass(2)">
          <img :src="`/assets/icons/status/chefHat.svg`" class="step-icon" />
        </div>
        <div class="step-label" :class="{ active: currentStep >= 2 }">
          {{ $t('screens.foodCourtOrder.status.preparing') }}
        </div>
      </div>

      <v-progress-linear
        class="step-connector"
        :color="currentStep >= 2 ? 'green' : '#D9D9D9'"
        height="3"
        :value="currentStep > 2 ? 100 : 0"
        :indeterminate="currentStep === 2"
      />

      <div class="step-item">
        <div class="step-circle" :class="getStepClass(3)">
          <img
            :src="`/assets/icons/status/circleCheck.svg`"
            class="step-icon"
          />
        </div>
        <div class="step-label" :class="{ active: currentStep >= 3 }">
          {{ $t('screens.foodCourtOrder.status.completed') }}
        </div>
      </div>
    </div>

    <v-fade-transition>
      <div
        v-if="orderStatus === 'partially_delivered'"
        class="mt-2 green rounded-xl px-3 py-3"
      >
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
          <span class="tp-text-body-white">
            {{ $t('screens.foodCourtOrder.status.partiallyDelivered') }}
          </span>
        </div>
      </div>
    </v-fade-transition>
  </div>
</template>

<script>
export default {
  name: 'OrderStatusV2',
  props: {
    orderStatus: {
      type: String,
      default: 'received',
      validator: value =>
        [
          'received',
          'preparing',
          'partially_delivered',
          'completed',
          'failed',
          'runner_delivery_pending',
        ].includes(value),
    },
  },
  computed: {
    currentStep() {
      const statusMap = {
        received: 1,
        preparing: 2,
        partially_delivered: 2,
        completed: 3,
        failed: 1, // Failed status shows at step 1 with error styling
        runner_delivery_pending: 1,
      };
      return statusMap[this.orderStatus] || 1;
    },
    orderActive() {
      const order = this.order.order;
      return order && order.status === 'active';
    },
    someItemsDelivered() {
      const order = this.order.order;

      return this.orderActive && order.items.some(item => item.is_delivered);
    },
  },
  methods: {
    getStepClass(step) {
      if (step < this.currentStep) {
        return 'completed';
      } else if (step === this.currentStep) {
        return 'active';
      } else {
        return 'inactive';
      }
    },
    getIconColor(step) {
      if (step < this.currentStep) {
        return 'white';
      } else if (step === this.currentStep) {
        return 'white';
      } else {
        return 'grey';
      }
    },
    getStepIcon(step) {
      const icons = {
        1: 'mdi-room-service-outline',
        2: 'mdi-chef-hat',
        3: 'mdi-check-circle-outline',
      };
      return icons[step] || 'mdi-circle';
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-order-stepper {
  padding-top: 10px;
  padding-bottom: 20px;
}

.step-icon {
  width: 24px;
  height: 24px;
}

.stepper-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background-color: #d9d9d9;
  transition: color 0.3s ease, transform 0.3s ease;

  &.completed {
    background-color: #4caf50;
  }

  &.active {
    background-color: #4caf50;
  }

  &.failed {
    background-color: #f44336;
    z-index: 2;
    position: relative;
  }

  &.inactive {
    background-color: #d9d9d9;
  }
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: #9e9e9e;
  text-align: center;
  transition: color 0.3s ease;
  margin-top: 4px;

  &.active {
    color: #333;
  }
}

.step-connector {
  flex: 1;
  height: 3px;
  background-color: #e0e0e0;
  margin: 0 -20px;
  position: relative;
  top: -12px;
  transition: background-color 0.3s ease;

  &.active {
    background-color: #4caf50;
  }
}

.failed-connector-line {
  position: absolute;
  top: 19px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #f44336;
  z-index: 1;
}
</style>
